import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type Severity = 'critical' | 'warning' | 'info';

export type Recommendation = {
  id: string;
  severity: Severity;
  title: string;
  summary: string;
  meta: string;
  time: string;
};

// Same dataset the Insights AI sidebar reads — kept in one place so the
// notification bell and the right rail can never drift apart.
export const RECOMMENDATIONS: readonly Recommendation[] = [
  {
    id: 'rec-1',
    severity: 'critical',
    title: 'Marlene 1-3-22138-31 — Rating-Drop > 1 Stern',
    summary: 'Durchschnitt fiel binnen 14 Tagen von 4,4 auf 3,2. Häufung von „Sohle löst sich" in DE/AT.',
    meta: 'DE, AT · 38 Reviews betroffen',
    time: 'vor 2 Std',
  },
  {
    id: 'rec-2',
    severity: 'critical',
    title: 'Lyra Sandalo — Negativ-Spike (35 %)',
    summary: 'Negativ-Anteil verdreifachte sich in den letzten 7 Tagen.',
    meta: 'IT · 27 Reviews · Sentiment 1–2 ★',
    time: 'vor 5 Std',
  },
  {
    id: 'rec-3',
    severity: 'warning',
    title: 'Italien — Negativ-Anteil > 30 %',
    summary: '32 % Negativ-Quote über alle Produkte hinweg, vor allem Größe & Komfort.',
    meta: 'IT, FR, ES · 142 Reviews betroffen',
    time: 'gestern',
  },
  {
    id: 'rec-4',
    severity: 'warning',
    title: 'Tora Sneaker — Hidden-Anteil ungewöhnlich',
    summary: 'Hidden-Quote 18 % vs. Katalog-Mittel 4 %. Mögliche Spam-Welle.',
    meta: 'DE · 19 Reviews · letzte 3 Tage',
    time: 'gestern',
  },
  {
    id: 'rec-5',
    severity: 'info',
    title: 'Neues Thema „Innensohle hart" erkannt',
    summary: 'Carlos clusterte 23 Reviews zu einem neuen Sentiment-Topic.',
    meta: 'Cross-Tenant · DE/IT/CH',
    time: 'vor 2 Tagen',
  },
];

const SEV_ICON: Record<Severity, string> = {
  critical: 'error',
  warning: 'warning',
  info: 'lightbulb',
};

const SEV_BG: Record<Severity, string> = {
  critical: 'rgba(231, 87, 50, 0.12)',
  warning: 'rgba(238, 136, 45, 0.12)',
  info: 'rgba(39, 172, 203, 0.12)',
};

const SEV_FG: Record<Severity, string> = {
  critical: 'var(--brand-red)',
  warning: 'var(--brand-orange)',
  info: 'var(--brand-cyan)',
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const visible = RECOMMENDATIONS.filter((r) => r.severity !== 'info');
  const criticalCount = RECOMMENDATIONS.filter((r) => r.severity === 'critical').length;
  const warningCount = RECOMMENDATIONS.filter((r) => r.severity === 'warning').length;

  function go(id?: string) {
    setOpen(false);
    navigate(id ? `/insights?rec=${id}` : '/insights');
  }

  return (
    <div className="notif" ref={ref}>
      <button
        type="button"
        className="iconbtn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Benachrichtigungen"
      >
        <span className="material-symbols-rounded">notifications</span>
        {visible.length > 0 && <span className="notif-badge">{visible.length}</span>}
      </button>
      {open && (
        <div className="notif-pop" role="dialog" aria-label="KI-Empfehlungen">
          <div className="notif-head">
            <div>
              <div className="notif-title">
                <span className="material-symbols-rounded">auto_awesome</span>
                KI-Empfehlungen
              </div>
              <div className="notif-sub">
                <span className="notif-critical-pill">{criticalCount} Critical</span>
                {' · '}
                {warningCount} Warning
              </div>
            </div>
            <button type="button" className="notif-close" onClick={() => setOpen(false)} aria-label="Schließen">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <div className="notif-list">
            {visible.map((r) => (
              <button
                type="button"
                key={r.id}
                className={`notif-item sev-${r.severity}`}
                onClick={() => go(r.id)}
              >
                <span className="notif-icon" style={{ background: SEV_BG[r.severity], color: SEV_FG[r.severity] }}>
                  <span className="material-symbols-rounded">{SEV_ICON[r.severity]}</span>
                </span>
                <span className="notif-body">
                  <span className="notif-row1">
                    <span className="notif-item-title">{r.title}</span>
                    <span className="notif-time">{r.time}</span>
                  </span>
                  <span className="notif-summary">{r.summary}</span>
                  <span className="notif-meta">{r.meta}</span>
                </span>
                <span className="material-symbols-rounded notif-chev">chevron_right</span>
              </button>
            ))}
          </div>
          <div className="notif-foot">
            <button type="button" className="notif-foot-btn" onClick={() => go()}>
              Alle Empfehlungen ansehen
              <span className="material-symbols-rounded">arrow_forward</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
